import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCountry } from '../lib/countries.js';
import {
  LayoutGrid,
  Users,
  Briefcase,
  Building2,
  LineChart as LineChartIcon,
  Bell,
  ChevronDown,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Hero visual — an analytics dashboard mock with three cards
 *  overlapping its lower edge.
 *
 *  Two things drive most of the decisions in here.
 *
 *  1. Scaling. Every size inside the panel is in `em`, and the panel's
 *     root font-size is a container query unit. The whole mock therefore
 *     scales with the column it lands in rather than with the viewport,
 *     which matters because the page container caps at max-w-7xl — a vw
 *     unit keeps growing after the column has stopped, and the mock
 *     would burst out of it. Layout widths stay in % / flex so only the
 *     type and spacing ride the em scale.
 *
 *  2. Colour. The home page runs under `.home-light`, a stylesheet that
 *     rewrites `text-white`, `text-slate-*` and the dark `bg-*` utilities
 *     with `!important` so dark-themed markup survives on a light page.
 *     This mock has genuinely dark surfaces on that light page, so every
 *     colour here is an arbitrary hex — those selectors cannot match a
 *     `text-[#…]` class, which is the only reliable way out. For the same
 *     reason there are no h1–h6 tags inside the dark cards: `.home-light`
 *     forces every heading to slate-900 and they would vanish.
 * ------------------------------------------------------------------ */

/* Mock figures. These belong to the dashboard being illustrated, not to
   Spectranox — it is a picture of a product screen. The two numbers that
   *are* claims about Spectranox live on the cards below and are taken
   from copy already on this page. */
const STATS = [
  { label: 'Total Revenue', to: 2475000, prefix: '₹', delta: '18.6%' },
  { label: 'New Customers', to: 1243, delta: '12.5%' },
  { label: 'Active Projects', to: 32, delta: '8.4%' },
  { label: 'Successful Orders', to: 3854, delta: '15.2%' },
];

/* ------------------------------------------------------------------ *
 *  Motion
 *
 *  Everything below is opt-out: if the visitor has asked for reduced
 *  motion the panel renders at its final state with no transition, no
 *  parallax and no counting, which is the whole point of the setting.
 * ------------------------------------------------------------------ */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(true); // assume calm until told otherwise
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return reduced;
}

/* Counts to `to` on an ease-out curve. Indian digit grouping, because the
   figure it renders is in rupees. */
function useCountUp(to, run, duration = 1600) {
  const [n, setN] = useState(run ? 0 : to);
  useEffect(() => {
    if (!run) { setN(to); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, run, duration]);
  return n.toLocaleString('en-IN');
}

/* Sums to 100. The reference art totalled 106, which reads as broken the
   moment anyone adds the legend up. */
const SERVICES = [
  { name: 'Web Development', pct: 45, color: '#2563eb' },
  { name: 'Mobile Apps', pct: 25, color: '#60a5fa' },
  { name: 'AI Solutions', pct: 18, color: '#a5b4fc' },
  { name: 'Other Services', pct: 12, color: '#cbd5e1' },
];

/* ------------------------------------------------------------------ *
 *  The rail is a real switcher: every icon opens its own view, so the
 *  panel behaves like the product it illustrates rather than a static
 *  picture of one.
 * ------------------------------------------------------------------ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (d) => String(d.getDate()).padStart(2, '0') + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();

/* The window the visitor actually sees: the last three months up to today,
   read off their clock rather than baked into the markup. */
function useDateWindow() {
  return useMemo(() => {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - 3);
    const ticks = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(from.getTime() + ((to - from) * i) / 4);
      return String(d.getDate()).padStart(2, '0') + ' ' + MONTHS[d.getMonth()];
    });
    return {
      label: fmtDay(from) + ' – ' + fmtDay(to),
      ticks,
      months: [MONTHS[from.getMonth()], MONTHS[to.getMonth()]],
    };
  }, []);
}

/* The six client projects the portfolio already carries, so these lists show
   real engagements rather than invented ones. */
const CLIENTS = [
  { name: 'OceanCore Technologies', country: 'in', work: 'Websites', pct: 100, tone: '#16a34a', state: 'Live' },
  { name: 'JobifyPL', country: 'pl', work: 'Apps, Websites', pct: 100, tone: '#16a34a', state: 'Live' },
  { name: 'S360 Consultancy', country: 'lv', work: 'Websites', pct: 100, tone: '#16a34a', state: 'Live' },
  { name: 'Silicon Minds', country: 'in', work: 'Trainings', pct: 82, tone: '#2563eb', state: 'Build' },
  { name: 'Nexora Tech', country: 'in', work: 'Websites', pct: 64, tone: '#2563eb', state: 'Build' },
  { name: 'Vastara Varaya', country: 'in', work: 'Commerce', pct: 38, tone: '#f59e0b', state: 'Design' },
];

const MARKETS = [
  { code: 'in', name: 'India', n: 18 },
  { code: 'pl', name: 'Poland', n: 4 },
  { code: 'lv', name: 'Latvia', n: 3 },
  { code: 'gb', name: 'United Kingdom', n: 2 },
  { code: 'ae', name: 'UAE', n: 2 },
];

/* Delivered per month across the window. Illustrative, like the revenue
   figures beside it: this is a picture of a product screen. */
const DELIVERY = [6, 9, 7, 11, 8, 12, 10, 14];

const RAIL = [
  { key: 'dashboard', Icon: LayoutGrid, label: 'Dashboard', title: 'Analytics Overview' },
  { key: 'clients', Icon: Users, label: 'Clients', title: 'Clients' },
  { key: 'engagements', Icon: Briefcase, label: 'Engagements', title: 'Engagements' },
  { key: 'markets', Icon: Building2, label: 'Markets', title: 'Markets' },
  { key: 'reports', Icon: LineChartIcon, label: 'Reports', title: 'Delivery Report' },
];

/* ---- the views the rail switches between (dashboard stays inline) ---- */

function Row({ children, i }) {
  return (
    <div
      className="sx-row flex items-center gap-[0.6em] rounded-[0.55em] bg-[#ffffff] px-[0.7em] py-[0.55em] ring-1 ring-[#0f172a]/[0.06]"
      style={{ '--r': i }}
    >
      {children}
    </div>
  );
}

function Flag({ code }) {
  const c = getCountry(code);
  if (!c) return null;
  return (
    <img
      src={c.flag}
      alt=""
      className="h-[0.72em] w-[1em] shrink-0 rounded-[0.12em] object-cover ring-1 ring-[#0f172a]/10"
    />
  );
}

function ClientsView() {
  return (
    <div className="space-y-[0.4em]">
      {CLIENTS.slice(0, 5).map((c, i) => (
        <Row key={c.name} i={i}>
          <span className="flex h-[1.6em] w-[1.6em] shrink-0 items-center justify-center rounded-[0.4em] bg-[#eff6ff] text-[0.62em] font-bold text-[#1d4ed8]">
            {c.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[0.8em] font-semibold text-[#0f172a]">{c.name}</span>
            <span className="mt-[0.1em] flex items-center gap-[0.35em] text-[0.66em] text-[#64748b]">
              <Flag code={c.country} />
              {c.work}
            </span>
          </span>
          <span
            className="shrink-0 rounded-[0.35em] px-[0.5em] py-[0.2em] text-[0.62em] font-bold"
            style={{ background: c.tone + '1a', color: c.tone }}
          >
            {c.state}
          </span>
        </Row>
      ))}
    </div>
  );
}

function EngagementsView() {
  return (
    <div className="space-y-[0.4em]">
      {CLIENTS.slice(0, 5).map((c, i) => (
        <Row key={c.name} i={i}>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-[0.5em]">
              <span className="truncate text-[0.8em] font-semibold text-[#0f172a]">{c.name}</span>
              <span className="shrink-0 text-[0.68em] font-bold text-[#0f172a]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {c.pct}%
              </span>
            </span>
            <span className="mt-[0.35em] block h-[0.35em] overflow-hidden rounded-full bg-[#e2e8f0]">
              <span className="sx-bar block h-full rounded-full" style={{ background: c.tone, '--w': c.pct + '%', '--r': i }} />
            </span>
          </span>
        </Row>
      ))}
    </div>
  );
}

function MarketsView() {
  const max = Math.max(...MARKETS.map((m) => m.n));
  return (
    <div className="space-y-[0.4em]">
      {MARKETS.map((m, i) => (
        <Row key={m.code} i={i}>
          <Flag code={m.code} />
          <span className="min-w-0 flex-1 truncate text-[0.8em] font-semibold text-[#0f172a]">{m.name}</span>
          <span className="h-[0.35em] w-[38%] shrink-0 overflow-hidden rounded-full bg-[#e2e8f0]">
            <span className="sx-bar block h-full rounded-full bg-[#2563eb]" style={{ '--w': (m.n / max) * 100 + '%', '--r': i }} />
          </span>
          <span className="w-[1.6em] shrink-0 text-right text-[0.72em] font-bold text-[#0f172a]" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {m.n}
          </span>
        </Row>
      ))}
    </div>
  );
}

function ReportsView({ months }) {
  const max = Math.max(...DELIVERY);
  return (
    <div className="rounded-[0.7em] bg-[#ffffff] p-[0.85em] ring-1 ring-[#0f172a]/[0.06]">
      <div className="flex items-baseline justify-between">
        <p className="text-[0.92em] font-semibold text-[#0f172a]">Projects delivered</p>
        <p className="text-[0.7em] text-[#64748b]">{months[0]} &ndash; {months[1]}</p>
      </div>
      {/* The bar's height is a percentage, so it needs an ancestor with a
          definite height to resolve against: flex-1 inside a fixed-height
          column supplies that, and the bar is absolute within it. */}
      <div className="mt-[0.9em] flex h-[8.6em] gap-[0.5em]">
        {DELIVERY.map((v, i) => (
          <span key={i} className="flex h-full min-w-0 flex-1 flex-col items-center gap-[0.3em]">
            <span className="text-[0.62em] font-bold text-[#0f172a]" style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
            <span className="relative w-full flex-1">
              <span
                className="sx-col absolute bottom-0 left-0 w-full rounded-t-[0.25em]"
                style={{ '--h': (v / max) * 100 + '%', '--r': i, background: i === DELIVERY.length - 1 ? '#2563eb' : '#bfdbfe' }}
              />
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Motion sheet
 *
 *  Scoped to .sx-hero and shipped with the component so the hero owns its
 *  own behaviour. Two rules run the whole thing:
 *
 *  - the resting tilt lives in custom properties, so the pointer handler
 *    only writes --px/--py and never fights the transform;
 *  - .is-calm (prefers-reduced-motion) zeroes every duration and delay
 *    rather than removing the rules, so the panel lands at its final
 *    state with nothing moving.
 * ------------------------------------------------------------------ */
const HERO_MOTION_CSS = `
.sx-hero { --px: 0; --py: 0; --rx: 0deg; --ry: 0deg; --rz: 0deg; --lift: 0px; }

/* Flat on phones and tablets: a tilted panel needs room either side to
   lean into, and below lg there is none. */
@media (min-width: 1024px) {
  .sx-hero { --rx: 3.4deg; --ry: -9deg; --rz: -1.4deg; --lift: 0px; }
}

.sx-hero .sx-stage {
  perspective: 1600px;
  perspective-origin: 60% 40%;
}

.sx-hero .sx-scene {
  /* Room under the panel for the cards to hang into. They overlap its lower
     edge by roughly a sixth, enough to tie the two together without hiding
     the revenue chart behind them. */
  padding-bottom: 7.2em;
  transform-style: preserve-3d;
  will-change: transform;
  transform:
    rotateX(calc(var(--rx) + var(--py) * -3.2deg))
    rotateY(calc(var(--ry) + var(--px) * 5.5deg))
    rotateZ(var(--rz))
    translateZ(0);
  transition: transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
}

/* ---- entrance ---- */
.sx-hero .sx-panel,
.sx-hero .sx-card {
  opacity: 0;
  transition:
    opacity 760ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
}
.sx-hero .sx-panel { transform: translate3d(0, 34px, -60px) scale(0.965); }
.sx-hero .sx-card  { transform: translate3d(0, 46px, 0) scale(0.94); transition-delay: calc(360ms + var(--i, 0) * 110ms); }

.sx-hero.is-ready .sx-panel { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
.sx-hero.is-ready .sx-card  { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }

/* Cards sit forward of the panel so the tilt separates them in depth. */
@media (min-width: 1024px) {
  .sx-hero.is-ready .sx-card { transform: translate3d(0, 0, 22px) scale(1); }
  .sx-hero.is-ready .sx-card:hover {
    transform: translate3d(0, -5px, 44px) scale(1.01);
    transition-delay: 0ms;
  }
}

/* ---- stat tiles ---- */
.sx-hero .sx-tile {
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 520ms ease-out, transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: calc(300ms + var(--i, 0) * 90ms);
}
.sx-hero.is-ready .sx-tile { opacity: 1; transform: none; }

/* ---- revenue chart draws itself ---- */
.sx-hero .sx-rev-line {
  stroke-dasharray: 420;
  stroke-dashoffset: 420;
  transition: stroke-dashoffset 1500ms cubic-bezier(0.33, 1, 0.68, 1) 520ms;
}
.sx-hero.is-ready .sx-rev-line { stroke-dashoffset: 0; }

.sx-hero .sx-rev-area { opacity: 0; transition: opacity 900ms ease-out 900ms; }
.sx-hero.is-ready .sx-rev-area { opacity: 1; }

.sx-hero .sx-rev-rule,
.sx-hero .sx-rev-dot { opacity: 0; transition: opacity 420ms ease-out 1500ms; }
.sx-hero.is-ready .sx-rev-rule,
.sx-hero.is-ready .sx-rev-dot { opacity: 1; }

/* ---- donut sweeps in, slice by slice ---- */
.sx-hero .sx-slice {
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
  transform: scale(0.72);
  transition:
    opacity 480ms ease-out,
    transform 620ms cubic-bezier(0.34, 1.36, 0.64, 1);
  transition-delay: var(--slice-delay, 0s);
}
.sx-hero.is-ready .sx-slice { opacity: 1; transform: scale(1); }

/* ---- switching views ----
   Each view is keyed on the active key, so React remounts it and these run
   fresh on every switch: the stage fades up, then rows and bars stagger in. */
.sx-hero .sx-view { animation: sx-view-in 380ms cubic-bezier(0.22, 1, 0.36, 1) both; }
@keyframes sx-view-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
}

.sx-hero .sx-row {
  animation: sx-row-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(var(--r, 0) * 55ms);
}
@keyframes sx-row-in {
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: none; }
}

/* Bars grow to their value rather than appearing at it. */
.sx-hero .sx-bar {
  width: var(--w, 0%);
  animation: sx-bar-in 760ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(140ms + var(--r, 0) * 55ms);
}
@keyframes sx-bar-in { from { width: 0%; } }

.sx-hero .sx-col {
  height: var(--h, 0%);
  animation: sx-col-in 640ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(120ms + var(--r, 0) * 55ms);
}
@keyframes sx-col-in { from { height: 0%; } }

/* The rail responds before the view has finished changing. */
.sx-hero .sx-rail-btn { transition: background-color 220ms ease, transform 220ms ease; }
.sx-hero .sx-rail-btn:active { transform: scale(0.92); }

/* ---- phones ----
   Three cards across a 335px illustration leaves each one 111px wide, where
   the body copy renders at 5px and cannot be read. The arrangement stays
   exactly as it is; the fine print goes, and what remains is set at a real
   size rather than a fraction of the mock's scale. */
@media (max-width: 639px) {
  .sx-hero .sx-card-body,
  .sx-hero .sx-card-link { display: none; }
  .sx-hero .sx-card { padding: 8px; border-radius: 10px; }
  .sx-hero .sx-card-title { font-size: 10.5px; line-height: 1.2; margin-top: 6px; }
  .sx-hero .sx-card-figure { font-size: 15px; margin-top: 4px; }
}

/* ---- reduced motion: land, do not travel ---- */
.sx-hero.is-calm .sx-scene,
.sx-hero.is-calm .sx-panel,
.sx-hero.is-calm .sx-card,
.sx-hero.is-calm .sx-tile,
.sx-hero.is-calm .sx-rev-line,
.sx-hero.is-calm .sx-rev-area,
.sx-hero.is-calm .sx-rev-rule,
.sx-hero.is-calm .sx-rev-dot,
.sx-hero.is-calm .sx-slice {
  transition-duration: 0ms !important;
  transition-delay: 0ms !important;
}
.sx-hero.is-calm .sx-scene { transform: none; }
.sx-hero.is-calm .sx-view,
.sx-hero.is-calm .sx-row,
.sx-hero.is-calm .sx-bar,
.sx-hero.is-calm .sx-col {
  animation: none !important;
}
`;


function StatTile({ stat, index, run }) {
  const n = useCountUp(stat.to, run);
  return (
    <div
      className="sx-tile rounded-[0.7em] bg-[#ffffff] p-[0.8em] ring-1 ring-[#0f172a]/[0.06]"
      style={{ '--i': index }}
    >
      <p className="truncate text-[0.8em] text-[#64748b]">{stat.label}</p>
      {/* Tabular figures, or the width jitters on every frame of the count. */}
      <p
        className="mt-[0.35em] truncate text-[1.25em] font-bold leading-none text-[#0f172a]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {stat.prefix || ''}{n}
      </p>
      <p className="mt-[0.5em] flex items-center gap-[0.3em] text-[0.72em] font-semibold text-[#15803d]">
        <TrendingUp className="h-[1em] w-[1em]" strokeWidth={2.6} />
        {stat.delta}
        <span className="font-normal text-[#64748b]">vs last month</span>
      </p>
    </div>
  );
}

/* Donut geometry: one circle per slice, offset by the running total. */
function Donut() {
  const R = 42;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
      {SERVICES.map((s, i) => {
        const len = (s.pct / 100) * C;
        const dash = `${len - 2} ${C - len + 2}`;
        const el = (
          <circle
            key={s.name}
            className="sx-slice"
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke={s.color}
            strokeWidth="15"
            strokeDasharray={dash}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            /* Each slice sweeps in behind the one before it. */
            style={{ '--slice-delay': `${0.55 + i * 0.13}s` }}
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}

function RevenueChart() {
  return (
    <svg viewBox="0 0 300 118" preserveAspectRatio="none" className="h-full w-full">
      <defs>
        <linearGradient id="sx-rev-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[26, 52, 78, 104].map((y) => (
        <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#eef2f7" strokeWidth="1" />
      ))}
      <path
        className="sx-rev-area"
        d="M0,96 C18,94 30,84 48,86 C66,88 78,66 98,68 C118,70 126,58 146,50 C166,42 176,56 196,44 C216,32 226,22 248,18 C268,14 284,10 300,6 L300,118 L0,118 Z"
        fill="url(#sx-rev-fill)"
      />
      <path
        className="sx-rev-line"
        d="M0,96 C18,94 30,84 48,86 C66,88 78,66 98,68 C118,70 126,58 146,50 C166,42 176,56 196,44 C216,32 226,22 248,18 C268,14 284,10 300,6"
        fill="none"
        stroke="#2563eb"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <line className="sx-rev-rule" x1="146" y1="50" x2="146" y2="118" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
      <circle className="sx-rev-dot" cx="146" cy="50" r="5.5" fill="#ffffff" stroke="#2563eb" strokeWidth="2.6" />
    </svg>
  );
}

export default function AnalyticsHeroVisual() {
  const reduced = usePrefersReducedMotion();
  const [ready, setReady] = useState(false);
  const hostRef = useRef(null);
  const sceneRef = useRef(null);

  /* One frame's grace so the browser has laid out and the entrance plays as a
     transition rather than as the first paint. */
  useEffect(() => {
    if (reduced) { setReady(true); return; }
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, [reduced]);

  /* Pointer parallax. The panel leans a few degrees toward the cursor and
     eases back when it leaves. Pointer only, desktop only: on a touch screen
     there is no hover to track, and on a phone the tilt is off anyway. */
  useEffect(() => {
    if (reduced) return;
    const host = hostRef.current;
    const scene = sceneRef.current;
    if (!host || !scene) return;
    if (!window.matchMedia('(min-width: 1024px) and (hover: hover)').matches) return;

    let raf = 0;
    let tx = 0, ty = 0, cx = 0, cy = 0;
    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    const onLeave = () => { tx = 0; ty = 0; };
    const tick = () => {
      /* Lerp rather than snap, so the panel glides instead of twitching. */
      cx += (tx - cx) * 0.075;
      cy += (ty - cy) * 0.075;
      scene.style.setProperty('--px', cx.toFixed(4));
      scene.style.setProperty('--py', cy.toFixed(4));
      raf = requestAnimationFrame(tick);
    };
    host.addEventListener('pointermove', onMove);
    host.addEventListener('pointerleave', onLeave);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerleave', onLeave);
    };
  }, [reduced]);

  const [view, setView] = useState('dashboard');
  const win = useDateWindow();
  const active = RAIL.find((r) => r.key === view) || RAIL[0];
  const counting = ready && !reduced && view === 'dashboard';

  return (
    <div
      ref={hostRef}
      className={`sx-hero relative mx-auto w-full max-w-[620px] xl:mx-0 xl:ml-auto xl:max-w-none${ready ? ' is-ready' : ''}${reduced ? ' is-calm' : ''}`}
      style={{ containerType: 'inline-size' }}
    >
      <style>{HERO_MOTION_CSS}</style>
      <div style={{ fontSize: 'clamp(5.2px, 2.1cqw, 17px)' }}>
        <div className="sx-stage">
        <div ref={sceneRef} className="sx-scene">
        {/* ---------- dashboard panel ---------- */}
        <div
          role="group"
          aria-label="Spectranox client dashboard preview"
          className="sx-panel relative z-10 flex overflow-hidden rounded-[1.5em] bg-[#ffffff] ring-1 ring-[#0f172a]/[0.07]"
          style={{ boxShadow: '0 40px 80px -30px rgba(15,23,42,0.35)' }}
        >
          {/* icon rail */}
          <div className="flex w-[3.9em] shrink-0 flex-col items-center gap-[1.1em] bg-[#0b1220] py-[1.5em]">
            <span className="flex h-[1.9em] w-[1.9em] items-center justify-center rounded-[0.55em] bg-[#2563eb]">
              <ShieldCheck className="h-[1.05em] w-[1.05em] text-[#ffffff]" strokeWidth={2.2} />
            </span>
            {RAIL.map(({ key, Icon, label }) => {
              const on = key === view;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  aria-pressed={on}
                  title={label}
                  className={`sx-rail-btn relative flex h-[1.9em] w-[1.9em] items-center justify-center rounded-[0.55em] transition-colors ${
                    on ? 'bg-[#1e293b]' : 'hover:bg-[#151d2e]'
                  }`}
                >
                  {/* The lit edge that marks the open view. */}
                  <span
                    aria-hidden="true"
                    className="absolute -left-[0.85em] top-1/2 h-[1.1em] w-[0.16em] -translate-y-1/2 rounded-full bg-[#60a5fa] transition-opacity duration-300"
                    style={{ opacity: on ? 1 : 0 }}
                  />
                  <Icon className={`h-[1.05em] w-[1.05em] ${on ? 'text-[#ffffff]' : 'text-[#64748b]'}`} strokeWidth={2} />
                  <span className="sr-only">{label}</span>
                </button>
              );
            })}
          </div>

          {/* body */}
          <div className="min-w-0 flex-1 bg-[#f8fafc] p-[1.35em]">
            {/* header */}
            <div className="flex items-center justify-between gap-[0.8em]">
              <p className="truncate text-[1.35em] font-bold leading-none text-[#0f172a]">
                {active.title}
              </p>
              <div className="flex shrink-0 items-center gap-[0.7em]">
                <span className="flex items-center gap-[0.5em] rounded-[0.5em] bg-[#ffffff] px-[0.75em] py-[0.45em] text-[0.82em] text-[#475569] ring-1 ring-[#0f172a]/[0.07]">
                  {win.label}
                  <ChevronDown className="h-[1em] w-[1em]" strokeWidth={2.4} />
                </span>
                <span className="relative">
                  <Bell className="h-[1.15em] w-[1.15em] text-[#475569]" strokeWidth={2.2} />
                  <span className="absolute -right-[0.1em] -top-[0.1em] h-[0.42em] w-[0.42em] rounded-full bg-[#ef4444]" />
                </span>
                <span className="h-[1.7em] w-[1.7em] rounded-full bg-gradient-to-br from-[#60a5fa] to-[#2563eb] ring-2 ring-[#ffffff]" />
              </div>
            </div>

            {/* A fixed stage: every view renders into the same height, so
                switching never shifts the cards that hang off the panel. */}
            <div key={view} className="sx-view mt-[1.15em] h-[16.4em] overflow-hidden">
              {view === 'dashboard' && (
                <>
                  <div className="grid grid-cols-4 gap-[0.7em]">
                    {STATS.map((s, i) => (
                      <StatTile key={s.label} stat={s} index={i} run={counting} />
                    ))}
                  </div>

                  <div className="mt-[0.7em] flex gap-[0.7em]">
                    <div className="min-w-0 flex-[1.65] rounded-[0.7em] bg-[#ffffff] p-[0.85em] ring-1 ring-[#0f172a]/[0.06]">
                      <div className="flex items-center justify-between gap-[0.5em]">
                        <p className="truncate text-[0.92em] font-semibold text-[#0f172a]">Revenue Overview</p>
                        <span className="flex shrink-0 items-center gap-[0.4em] rounded-[0.45em] bg-[#f1f5f9] px-[0.6em] py-[0.3em] text-[0.72em] text-[#475569]">
                          Last 3 months
                          <ChevronDown className="h-[0.9em] w-[0.9em]" strokeWidth={2.4} />
                        </span>
                      </div>
                      <div className="relative mt-[0.6em] h-[7.2em]">
                        <RevenueChart />
                        <span className="absolute left-[48.7%] top-[16%] -translate-x-1/2 whitespace-nowrap rounded-[0.4em] bg-[#0f172a] px-[0.55em] py-[0.28em] text-[0.7em] font-semibold text-[#ffffff]">
                          ₹24,75,000
                        </span>
                      </div>
                      <div className="mt-[0.45em] flex justify-between text-[0.68em] text-[#64748b]">
                        {win.ticks.map((d, i) => (
                          <span key={i}>{d}</span>
                        ))}
                      </div>
                    </div>

                    <div className="min-w-0 flex-1 rounded-[0.7em] bg-[#ffffff] p-[0.85em] ring-1 ring-[#0f172a]/[0.06]">
                      <p className="truncate text-[0.92em] font-semibold text-[#0f172a]">Top Services</p>
                      <div className="relative mx-auto mt-[0.5em] h-[5.2em] w-[5.2em]">
                        <Donut />
                        <span className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-[0.72em] font-bold leading-none text-[#0f172a]">₹24.7L</span>
                          <span className="mt-[0.2em] text-[0.6em] leading-none text-[#64748b]">Total</span>
                        </span>
                      </div>
                      <div className="mt-[0.65em] space-y-[0.35em]">
                        {SERVICES.map((s) => (
                          <div key={s.name} className="flex items-center gap-[0.45em] text-[0.7em]">
                            <span className="h-[0.5em] w-[0.5em] shrink-0 rounded-full" style={{ background: s.color }} />
                            <span className="truncate text-[#475569]">{s.name}</span>
                            <span className="ml-auto shrink-0 font-semibold text-[#0f172a]">{s.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {view === 'clients' && <ClientsView />}
              {view === 'engagements' && <EngagementsView />}
              {view === 'markets' && <MarketsView />}
              {view === 'reports' && <ReportsView months={win.months} />}
            </div>
          </div>
        </div>

        {/* ---------- three cards over the panel's lower edge ----------
            Same arrangement at every width. The panel reserves a 9.6em band
            below itself and these sit in it, overlapping its lower edge by
            whatever the cards are taller than the band. Both measures are in
            em, so the overlap holds its proportion as the mock scales. */}
        <div className="absolute inset-x-[-1.5%] bottom-0 z-20 grid grid-cols-3 gap-[0.8em]">
          {/* Each card is itself the link, not the "Learn more" line inside it:
            at phone scale that line is 13px tall, well under the 24px minimum
            target size, while the card is 116x83. The label stays where it
            was, so nothing moves. */}
        {/* dark — security */}
          <Link
            to="/services/cyber-security"
            className="sx-card group flex flex-col rounded-[0.9em] bg-[#0b1220] p-[0.9em] ring-1 ring-[#0f172a]/10"
            style={{ '--i': 0, boxShadow: '0 26px 50px -24px rgba(15,23,42,0.55)' }}
          >
            <span className="flex h-[1.85em] w-[1.85em] items-center justify-center rounded-[0.55em] bg-[#ffffff]/10">
              <ShieldCheck className="h-[1.15em] w-[1.15em] text-[#ffffff]" strokeWidth={2.2} />
            </span>
            <p className="sx-card-title mt-[0.55em] text-[0.95em] font-bold leading-tight text-[#ffffff]">
              Enterprise Grade
              <br />
              Security
            </p>
            <p className="sx-card-body mt-[0.35em] text-[0.76em] leading-snug text-[#cbd5e1]">
              Your data and systems are always protected.
            </p>
            <span className="sx-card-link mt-auto inline-flex items-center gap-[0.35em] pt-[0.55em] text-[0.78em] font-semibold text-[#93c5fd] group-hover:text-[#bfdbfe]">
              Learn more
              <ArrowRight className="h-[0.95em] w-[0.95em]" strokeWidth={2.6} />
            </span>
          </Link>

          {/* light — the 90% is this page's own "up to 90% lower development
              cost" claim, not a new one */}
          <Link
            to="/about"
            className="sx-card group flex flex-col rounded-[0.9em] bg-[#ffffff] p-[0.9em] ring-1 ring-[#0f172a]/[0.07]"
            style={{ '--i': 1, boxShadow: '0 26px 50px -24px rgba(15,23,42,0.30)' }}
          >
            <div className="flex items-center gap-[0.7em]">
              <span className="relative h-[2.6em] w-[2.6em] shrink-0">
                <svg viewBox="0 0 44 44" className="h-full w-full -rotate-90">
                  <circle cx="22" cy="22" r="18" fill="none" stroke="#e2e8f0" strokeWidth="5" />
                  <circle
                    cx="22"
                    cy="22"
                    r="18"
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray={`${0.9 * 2 * Math.PI * 18} ${2 * Math.PI * 18}`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[0.72em] font-bold text-[#0f172a]">
                  90%
                </span>
              </span>
              <p className="sx-card-title text-[1em] font-bold leading-tight text-[#0f172a]">
                Lower
                <br />
                Build Cost
              </p>
            </div>
            <p className="sx-card-body mt-[0.45em] text-[0.76em] leading-snug text-[#475569]">
              Versus a traditional agency build.
            </p>
            <span className="sx-card-link mt-auto inline-flex items-center gap-[0.35em] pt-[0.55em] text-[0.78em] font-semibold text-[#1d4ed8] group-hover:text-[#1e40af]">
              View details
              <ArrowRight className="h-[0.95em] w-[0.95em]" strokeWidth={2.6} />
            </span>
          </Link>

          {/* blue — the way into the portfolio.

              It used to show the client count, which the trust row beside it
              and the track-record section below it both also stated. Three
              copies of one number is not three pieces of evidence. It points
              at the work instead. */}
          <Link
            to="/portfolio"
            className="sx-card group flex flex-col rounded-[0.9em] p-[0.9em]"
            style={{
              '--i': 2,
              background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
              boxShadow: '0 26px 50px -24px rgba(37,99,235,0.60)',
            }}
          >
            <span className="flex h-[1.85em] w-[1.85em] items-center justify-center rounded-[0.55em] bg-[#ffffff]/20">
              <Users className="h-[1.15em] w-[1.15em] text-[#ffffff]" strokeWidth={2.2} />
            </span>
            <p className="sx-card-figure mt-[0.55em] text-[1.35em] font-extrabold leading-none text-[#ffffff]">Live</p>
            <p className="sx-card-title mt-[0.2em] text-[0.88em] font-bold leading-tight text-[#ffffff]">
              Client Work
            </p>
            <p className="sx-card-body mt-[0.3em] text-[0.76em] leading-snug text-[#dbeafe]">
              Sites, apps and systems running in production today.
            </p>
            <span className="sx-card-link mt-auto inline-flex items-center gap-[0.35em] pt-[0.55em] text-[0.78em] font-semibold text-[#ffffff] group-hover:text-[#dbeafe]">
              View portfolio
              <ArrowRight className="h-[0.95em] w-[0.95em]" strokeWidth={2.6} />
            </span>
          </Link>
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
